import { BALL_RADIUS } from '../constants';

/**
 * Residual Correction Model — RBF (Radial Basis Function) Network
 *
 * A small learned model that corrects systematic biases in the physics engine.
 * Trained against TrackMan PGA Tour data to eliminate per-club carry, apex,
 * and landing angle errors.
 *
 * Architecture:
 *   Input:  initial spin factor S₀ = (ω × R) / ball_speed_ms
 *   Hidden: 7 Gaussian RBF kernels (one per calibration club)
 *   Output: (cdMult, clMult, ddMult) — multipliers for drag, lift, and descent drag boost
 *
 * The correction is smooth (Gaussian interpolation) so it generalizes to
 * arbitrary S₀ values between calibrated clubs (e.g., 6-iron, 4-iron, lob wedge).
 *
 * Calibration points are found by gradient-free optimization that minimizes
 * carry + apex error against TrackMan Tour averages.
 */

export interface AeroCorrection {
  cdMult: number;  // drag multiplier (1.0 = no change, >1 = more drag)
  clMult: number;  // lift multiplier (1.0 = no change, >1 = more lift)
  ddMult: number;  // descent dampening multiplier (1.0 = no change, >1 = steeper landing)
}

/** A single RBF calibration point */
interface CalibrationPoint {
  s0: number;       // launch spin factor
  cdMult: number;   // optimal drag multiplier
  clMult: number;   // optimal lift multiplier
  ddMult: number;   // optimal descent dampening multiplier
  sigma: number;    // Gaussian kernel width
}

/**
 * Calibration data — found by auto-calibrator (see tests/calibration/).
 *
 * Each point represents the optimal Cd/Cl correction for a specific club's
 * launch spin factor. Between points, the corrections blend smoothly via
 * Gaussian-weighted interpolation.
 *
 * Format: [S₀, cdMult, clMult, ddMult, sigma]
 *   S₀ = spin_factor at launch
 *   cdMult = drag correction (>1 = more drag = shorter carry)
 *   clMult = lift correction (>1 = more lift = higher apex)
 *   ddMult = descent drag boost (>1 = steeper landing, <1 = shallower)
 *   sigma = kernel width (how far this correction influences neighboring S₀)
 */
// Auto-calibrated with 3-parameter optimizer (carry + apex + landing angle)
// Generated by tests/calibration/autoCalibrate.test.ts (RBF-aware, descent drag boost)
// <0.1% carry/apex error, <2° landing angle error across all 7 clubs
// Auto-tuned against 111 real TrackMan configs + 7 Tour anchors (2026-03-24)
// Total loss: 21.3 → 17.9 (-16%), landing angle: 1.61° → 1.22°, Tour carry: 0.92% → 0.14%
const CALIBRATION_POINTS: CalibrationPoint[] = [
  { s0: 0.080, cdMult: 0.9468, clMult: 1.0527, ddMult: 0.6827, sigma: 0.030 },  // driver
  { s0: 0.116, cdMult: 0.9654, clMult: 0.9569, ddMult: 2.1720, sigma: 0.030 },  // 3-wood
  { s0: 0.195, cdMult: 0.9710, clMult: 1.0830, ddMult: 2.2260, sigma: 0.040 },  // 5-iron
  { s0: 0.200, cdMult: 0.9920, clMult: 0.7450, ddMult: 2.4020, sigma: 0.030 },  // low-S₀ 7i / long-iron overlap
  { s0: 0.296, cdMult: 1.0109, clMult: 1.0202, ddMult: 1.3677, sigma: 0.040 },  // 7-iron (Tour)
  { s0: 0.397, cdMult: 1.0780, clMult: 1.1425, ddMult: 0.8435, sigma: 0.045 },  // 9-iron
  { s0: 0.457, cdMult: 0.9131, clMult: 0.7581, ddMult: 0.3450, sigma: 0.045 },  // PW (Tour)
  { s0: 0.520, cdMult: 1.0870, clMult: 1.1360, ddMult: 1.2910, sigma: 0.040 },  // 50-54W (tuned against 20+ real wedge configs)
  { s0: 0.593, cdMult: 0.9494, clMult: 0.9977, ddMult: 0.3000, sigma: 0.045 },  // SW (Tour)
];

/**
 * Compute the initial spin factor S₀ from launch conditions.
 * This is used to index into the correction model.
 */
export function launchSpinFactor(spinRateRpm: number, ballSpeedMs: number): number {
  const omegaRad = spinRateRpm * (2 * Math.PI / 60);
  return (omegaRad * BALL_RADIUS) / Math.max(ballSpeedMs, 1.0);
}

/**
 * Get aerodynamic correction for a given launch spin factor.
 *
 * Uses Gaussian-weighted (RBF) interpolation across calibration points.
 * Returns multipliers for Cd and Cl that correct systematic model bias.
 *
 * For S₀ values far from any calibration point, corrections smoothly
 * decay toward (1.0, 1.0) — no correction for uncalibrated regimes.
 */
export function getAeroCorrection(s0: number): AeroCorrection {
  let totalWeight = 0;
  let cdWeighted = 0;
  let clWeighted = 0;
  let ddWeighted = 0;

  for (const pt of CALIBRATION_POINTS) {
    const d = s0 - pt.s0;
    const w = Math.exp(-(d * d) / (2 * pt.sigma * pt.sigma));
    totalWeight += w;
    cdWeighted += w * pt.cdMult;
    clWeighted += w * pt.clMult;
    ddWeighted += w * pt.ddMult;
  }

  if (totalWeight < 1e-10) {
    return { cdMult: 1.0, clMult: 1.0, ddMult: 1.0 };
  }

  return {
    cdMult: cdWeighted / totalWeight,
    clMult: clWeighted / totalWeight,
    ddMult: ddWeighted / totalWeight,
  };
}

/**
 * Update calibration points programmatically.
 * Used by the auto-calibrator to find optimal corrections.
 */
export function setCalibrationPoints(points: Array<{
  s0: number; cdMult: number; clMult: number; ddMult: number; sigma: number;
}>): void {
  CALIBRATION_POINTS.length = 0;
  for (const p of points) {
    CALIBRATION_POINTS.push({ ...p });
  }
}

/**
 * Get current calibration points (for inspection/export).
 */
export function getCalibrationPoints(): ReadonlyArray<Readonly<CalibrationPoint>> {
  return CALIBRATION_POINTS;
}
